/**
 * Social-auth buttons (Google + Facebook + Apple).
 *
 * Google is wired through Emergent-managed OAuth (the caller passes the
 * onGoogle handler). Facebook + Apple are intentionally STUBS for now —
 * tapping either shows a "Coming soon" toast and tells the user what's
 * needed to enable the provider. This keeps the UI honest while we wait
 * for credentials.
 *
 * To activate Facebook later we'll need:
 *   - FACEBOOK_APP_ID, FACEBOOK_APP_SECRET (env)
 *   - Facebook Developer app with the OAuth redirect URI registered
 * To activate Apple later we'll need:
 *   - APPLE_CLIENT_ID (Services ID), APPLE_TEAM_ID, APPLE_KEY_ID,
 *     APPLE_PRIVATE_KEY  (env)
 *   - Apple Developer Program enrollment + Sign-In-with-Apple capability
 *     and the Services ID redirect URI registered
 */
import { toast } from "sonner";

const PROVIDER_HINTS = {
  facebook: "Available when Facebook OAuth (App ID + Secret) is connected.",
  apple: "Available when Apple Developer (Services ID + Key) is connected.",
};

export function comingSoonAuth(provider) {
  toast.message("Coming soon", {
    description: PROVIDER_HINTS[provider] ?? `Available when ${provider} OAuth is connected.`,
  });
}

function SocialButton({ testId, onClick, label, icon, accentColor }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="w-full px-5 py-3 rounded-full border border-white/15 text-zinc-100 hover:bg-white/5 transition flex items-center justify-center gap-2"
      style={accentColor ? { borderColor: `${accentColor}55` } : undefined}
    >
      {icon}
      {label}
    </button>
  );
}

const GoogleIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
    <path fill="#fff" d="M21.35 11.1H12v2.9h5.35c-.23 1.5-1.74 4.4-5.35 4.4-3.21 0-5.83-2.66-5.83-5.93s2.62-5.93 5.83-5.93c1.83 0 3.06.78 3.76 1.45l2.57-2.47C16.86 4.07 14.69 3 12 3 6.97 3 2.9 7.05 2.9 12.07c0 5.03 4.07 9.07 9.1 9.07 5.25 0 8.73-3.69 8.73-8.89 0-.6-.06-1.05-.13-1.5z"/>
  </svg>
);

const FacebookIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
    <path fill="#1877F2" d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073c0 6.019 4.388 11.005 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.668 4.533-4.668 1.313 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.078 24 18.092 24 12.073z"/>
  </svg>
);

const AppleIcon = (
  <svg width="18" height="18" viewBox="0 0 384 512" aria-hidden>
    <path fill="#fff" d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zM246.2 100.8c19.2-22.8 17.4-43.6 16.8-51.1-16.9 1-36.5 11.5-47.7 24.5-12.3 13.9-19.5 31.1-17.9 50.4 18.3 1.4 35-8 48.8-23.8z"/>
  </svg>
);

export function SocialAuthButtons({ onGoogle, testIdPrefix = "login" }) {
  return (
    <div className="space-y-2.5">
      <SocialButton
        testId={`${testIdPrefix}-google`}
        onClick={onGoogle}
        label="Continue with Google"
        icon={GoogleIcon}
      />
      <SocialButton
        testId={`${testIdPrefix}-facebook`}
        onClick={() => comingSoonAuth("facebook")}
        label="Continue with Facebook"
        icon={FacebookIcon}
        accentColor="#1877F2"
      />
      <SocialButton
        testId={`${testIdPrefix}-apple`}
        onClick={() => comingSoonAuth("apple")}
        label="Continue with Apple"
        icon={AppleIcon}
      />
    </div>
  );
}
